import { useEffect, useState } from "react";
import { Archive, Download, Trash2, RotateCcw, Clock, FileArchive, Database, Settings2, Image, Palette, Globe } from "lucide-react";
import api from "@/api";
import { toast } from "sonner";

const TYPES = [
  { key: "full", label: "Full Backup", desc: "Database + config + React source + actual media files — fully recoverable", icon: Archive },
  { key: "database", label: "Database Only", desc: "All collections as JSON (restorable)", icon: Database },
  { key: "config", label: "Config Only", desc: "Settings, menus, banners, redirects", icon: Settings2 },
  { key: "media_refs", label: "Media Backup", desc: "Actual image/video files + manifest — works even if this server is gone", icon: Image },
  { key: "theme", label: "Theme Export", desc: "Full React source code + design tokens", icon: Palette },
  { key: "wordpress", label: "WordPress Migration", desc: "Installable WooCommerce theme + product CSV + content data", icon: Globe },
];

const fmtSize = (b) => b > 1048576 ? `${(b / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round((b || 0) / 1024))} KB`;
const fmtDate = (d) => new Date(d).toLocaleString("en-IN", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });

export default function AdminBackups() {
  const [backups, setBackups] = useState(null);
  const [creating, setCreating] = useState("");
  const [downloading, setDownloading] = useState("");

  const load = () => api.get("/admin/backups").then(({ data }) => setBackups(data)).catch((e) => toast.error(e.friendlyMessage));
  useEffect(() => { load(); }, []);

  const create = async (type) => {
    setCreating(type);
    try {
      await api.post("/admin/backups", { type }, { timeout: 120000 });
      toast.success(`${TYPES.find((t) => t.key === type)?.label} created`);
      load();
    } catch (err) { toast.error(err.friendlyMessage); } finally { setCreating(""); }
  };

  const download = async (b) => {
    setDownloading(b.id);
    try {
      const { data } = await api.get(`/admin/backups/${b.id}/download`, { responseType: "blob", timeout: 120000 });
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `tarishq-${b.type}-${(b.created_at || "").slice(0, 10)}.zip`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) { toast.error(err.friendlyMessage); } finally { setDownloading(""); }
  };

  const restore = async (b) => {
    if (!window.confirm(`Restore "${b.type}" backup from ${fmtDate(b.created_at)}?\n\nCurrent data in those collections will be REPLACED. A safety restore-point is created automatically. User accounts are never touched.`)) return;
    try {
      const { data } = await api.post(`/admin/backups/${b.id}/restore`, {}, { timeout: 120000 });
      toast.success(`Restored: ${data.restored_collections.join(", ")}`);
      load();
    } catch (err) { toast.error(err.friendlyMessage); }
  };

  const del = async (b) => {
    if (!window.confirm("Delete this backup permanently?")) return;
    try { await api.delete(`/admin/backups/${b.id}`); toast.success("Backup deleted"); load(); }
    catch (err) { toast.error(err.friendlyMessage); }
  };

  return (
    <div className="max-w-5xl" data-testid="admin-backups">
      <div className="mb-6">
        <h1 className="font-serif text-3xl">Backups & Export</h1>
        <p className="text-sm text-[#6E6D6A] mt-1">One-click backups, WordPress migration export, and theme portability. Scheduled daily/weekly backups run automatically.</p>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
        {TYPES.map((t) => (
          <div key={t.key} className="bg-white border border-[#E5DEC9] p-5 flex flex-col" data-testid={`backup-type-${t.key}`}>
            <t.icon size={20} className="text-[#C5A059] mb-2" />
            <p className="font-semibold text-sm">{t.label}</p>
            <p className="text-xs text-[#6E6D6A] mt-1 mb-4 flex-1">{t.desc}</p>
            <button onClick={() => create(t.key)} disabled={!!creating} data-testid={`backup-create-${t.key}`} className="tq-btn-dark !py-2.5 w-full">
              {creating === t.key ? "Creating…" : "Create & Download Later"}
            </button>
          </div>
        ))}
      </div>

      <div className="flex items-center gap-2 mb-3">
        <Clock size={15} className="text-[#C5A059]" />
        <p className="text-xs text-[#6E6D6A]">Automatic schedule: <span className="font-semibold text-[#121212]">database backup daily</span> · <span className="font-semibold text-[#121212]">full backup weekly</span> · last 10 per type retained.</p>
      </div>

      <h2 className="font-serif text-xl mb-3">Backup History</h2>
      {!backups ? <div className="h-40 bg-white animate-pulse" /> : backups.length === 0 ? (
        <div className="bg-white border border-[#E5DEC9] p-10 text-center text-sm text-[#6E6D6A]" data-testid="backups-empty">
          <FileArchive size={28} className="mx-auto text-[#C5A059] mb-2" />
          No backups yet. Create your first backup above.
        </div>
      ) : (
        <div className="bg-white border border-[#E5DEC9] divide-y divide-[#F4EFE6]">
          {backups.map((b) => (
            <div key={b.id} className="p-4 flex flex-wrap items-center gap-3 text-sm" data-testid={`backup-row-${b.id}`}>
              <span className="font-semibold capitalize w-36">{TYPES.find((t) => t.key === b.type)?.label || b.type}</span>
              <span className="text-xs text-[#6E6D6A]">{fmtDate(b.created_at)}</span>
              <span className="text-xs text-[#9a938a]">{fmtSize(b.size)}</span>
              <span className="text-xs text-[#9a938a] flex-1">by {b.by}</span>
              <div className="flex gap-1.5">
                <button onClick={() => download(b)} disabled={downloading === b.id} data-testid={`backup-download-${b.id}`} aria-label="Download backup" className="p-2 hover:bg-[#F4EFE6]" title="Download ZIP">
                  <Download size={15} className={downloading === b.id ? "animate-bounce" : ""} />
                </button>
                {["database", "config", "full"].includes(b.type) && (
                  <button onClick={() => restore(b)} data-testid={`backup-restore-${b.id}`} aria-label="Restore backup" className="p-2 hover:bg-[#F4EFE6] text-[#8C7A5E]" title="Restore"><RotateCcw size={15} /></button>
                )}
                <button onClick={() => del(b)} data-testid={`backup-delete-${b.id}`} aria-label="Delete backup" className="p-2 text-[#8B1E21] hover:bg-[#8B1E21]/10" title="Delete"><Trash2 size={15} /></button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
